import { trace } from '@opentelemetry/api'

type Primitive = string | number | boolean | null | undefined

function parseOtelResourceAttributes(env: string | undefined) {
  const otel = env ?? ''
  const serviceName = /(?:^|,)service\.name=([^,]+)/.exec(otel)?.[1]
  const svcNs = /(?:^|,)service\.namespace=([^,]+)/.exec(otel)?.[1]
  const envName = /(?:^|,)deployment\.environment=([^,]+)/.exec(otel)?.[1]
  return {
    serviceName,
    serviceNamespace: svcNs,
    environment: envName,
  }
}

function getActiveSpanMeta() {
  try {
    const ctx = trace.getActiveSpan()?.spanContext()
    if (!ctx) return {}
    return { traceId: ctx.traceId, spanId: ctx.spanId }
  } catch {
    return {}
  }
}

/**
 * Build bindings for `logger.child()` enriched with OpenTelemetry span correlation and
 * stable resource attributes (service namespace, environment, logical name).
 * Usage: `logger.child(withTraceChild({ scope: 'procedure', ... }))`
 * Caller-specified bindings take precedence over auto meta when keys collide.
 */
export function withTraceChild(bindings: Record<string, Primitive | Record<string, unknown>> = {}) {
  const spanMeta = getActiveSpanMeta()

  const parsed = parseOtelResourceAttributes(process.env.OTEL_RESOURCE_ATTRIBUTES)
  const serviceName = process.env.OTEL_SERVICE_NAME ?? parsed.serviceName

  const resourceMeta = {
    environment: parsed.environment ?? process.env.STAGE ?? process.env.SST_STAGE,
  } as Record<string, string | Record<string, string>>
  if (serviceName && parsed.serviceNamespace) {
    resourceMeta.service = {
      name: serviceName,
      namespace: parsed.serviceNamespace,
    }
  }

  // Merge order: auto meta first, then caller so caller can override if needed
  return {
    ...spanMeta,
    ...resourceMeta,
    ...bindings,
  }
}
